import { FOOTER_PREMIUM } from "../common/footer";
import { PULSE_HEADER } from "../common/header";

/**
 * World-Class Monthly Analytics Template
 * Features: Milestone badges, colorful stat cards, engagement breakdown, top queries table, recommendations
 */
export const monthlyAnalytics = `
<mjml>
  <mj-head>
    <mj-title>Your Monthly Analytics Report - {{month_name}} {{year}}</mj-title>
    <mj-preview>In-depth analysis of your chatbot performance for {{month_name}}</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .trend-up { color: #10B981; font-weight: 600; }
      .trend-down { color: #EF4444; font-weight: 600; }
      .milestone-badge { background: linear-gradient(135deg, #F59E0B, #D97706); color: white; padding: 8px 20px; border-radius: 25px; font-size: 13px; font-weight: 700; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${PULSE_HEADER("Monthly Analytics", "{{month_name}} {{year}}")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hello {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Here's your comprehensive monthly analytics report for {{product_name}}. This report covers all activity from {{report_start_date}} to {{report_end_date}}.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Monthly Milestone -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#F59E0B" font-weight="700" text-transform="uppercase" letter-spacing="1px" align="center" padding-bottom="12px">
          Monthly Achievement
        </mj-text>
        <mj-text align="center" padding-bottom="15px">
          <span style="background: linear-gradient(135deg, #F59E0B, #D97706); color: white; padding: 10px 25px; border-radius: 25px; font-size: 15px; font-weight: 700; display: inline-block;">&#127942; {{milestone_badge}}</span>
        </mj-text>
        <mj-text font-size="14px" color="#64748b" align="center">
          Congratulations on reaching this milestone!
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Key Metrics Header -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Overview
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          Key Metrics This Month
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Metrics Row -->
    <mj-section background-color="#ffffff" padding="10px 20px 30px" border-radius="0 0 16px 16px">
      <mj-column width="32%" padding="10px" background-color="#f0f9ff" border-radius="12px">
        <mj-text align="center" font-size="32px" color="#1939B7" font-weight="700" padding-bottom="0">
          {{total_conversations_month}}
        </mj-text>
        <mj-text align="center" font-size="12px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Conversations
        </mj-text>
        <mj-text align="center" font-size="13px" css-class="{{conv_trend_class}}" padding-top="0">
          {{conv_month_change}} vs last month
        </mj-text>
      </mj-column>
      <mj-column width="32%" padding="10px" background-color="#ecfdf5" border-radius="12px">
        <mj-text align="center" font-size="32px" color="#10B981" font-weight="700" padding-bottom="0">
          {{total_leads_month}}
        </mj-text>
        <mj-text align="center" font-size="12px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Leads Captured
        </mj-text>
        <mj-text align="center" font-size="13px" css-class="{{leads_trend_class}}" padding-top="0">
          {{leads_month_change}} vs last month
        </mj-text>
      </mj-column>
      <mj-column width="32%" padding="10px" background-color="#faf5ff" border-radius="12px">
        <mj-text align="center" font-size="32px" color="#8B5CF6" font-weight="700" padding-bottom="0">
          {{avg_response_time}}
        </mj-text>
        <mj-text align="center" font-size="12px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Avg Response
        </mj-text>
        <mj-text align="center" font-size="13px" css-class="{{response_trend_class}}" padding-top="0">
          {{response_month_change}} vs last month
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Engagement Breakdown -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Breakdown
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Engagement Analysis
        </mj-text>
      </mj-column>
    </mj-section>

    <mj-section background-color="#ffffff" padding="0 30px 30px" border-radius="0 0 16px 16px">
      <mj-column width="48%" padding="15px" background-color="#f8fafc" border-radius="12px">
        <mj-text font-size="14px" color="#1939B7" font-weight="700" padding-bottom="15px">
          &#127760; By Channel
        </mj-text>
        <mj-text font-size="14px" color="#334155" line-height="2">
          <span style="display: inline-block; width: 12px; height: 12px; background: #1939B7; border-radius: 3px; margin-right: 8px;"></span>Website: {{channel_website}}%<br>
          <span style="display: inline-block; width: 12px; height: 12px; background: #10B981; border-radius: 3px; margin-right: 8px;"></span>WhatsApp: {{channel_whatsapp}}%<br>
          <span style="display: inline-block; width: 12px; height: 12px; background: #3B82F6; border-radius: 3px; margin-right: 8px;"></span>Facebook: {{channel_facebook}}%<br>
          <span style="display: inline-block; width: 12px; height: 12px; background: #64748B; border-radius: 3px; margin-right: 8px;"></span>Other: {{channel_other}}%
        </mj-text>
      </mj-column>
      <mj-column width="48%" padding="15px" background-color="#f8fafc" border-radius="12px">
        <mj-text font-size="14px" color="#1939B7" font-weight="700" padding-bottom="15px">
          &#128241; By Device
        </mj-text>
        <mj-text font-size="14px" color="#334155" line-height="2">
          <span style="display: inline-block; width: 12px; height: 12px; background: #8B5CF6; border-radius: 3px; margin-right: 8px;"></span>Mobile: {{device_mobile}}%<br>
          <span style="display: inline-block; width: 12px; height: 12px; background: #0EA5E9; border-radius: 3px; margin-right: 8px;"></span>Desktop: {{device_desktop}}%<br>
          <span style="display: inline-block; width: 12px; height: 12px; background: #F59E0B; border-radius: 3px; margin-right: 8px;"></span>Tablet: {{device_tablet}}%
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Top Queries -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          User Queries
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Top 5 Query Topics
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b; text-transform: uppercase;">Topic</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Count</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Resolved</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #1939B7; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">1</span>
              {{query_1_topic}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{query_1_count}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #10B981;">{{query_1_resolution}}%</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #64748B; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">2</span>
              {{query_2_topic}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{query_2_count}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #10B981;">{{query_2_resolution}}%</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #94A3B8; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">3</span>
              {{query_3_topic}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{query_3_count}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #10B981;">{{query_3_resolution}}%</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #CBD5E1; color: #475569; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">4</span>
              {{query_4_topic}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{query_4_count}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #10B981;">{{query_4_resolution}}%</td>
          </tr>
          <tr>
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #E2E8F0; color: #475569; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">5</span>
              {{query_5_topic}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{query_5_count}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #10B981;">{{query_5_resolution}}%</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Recommendations -->
    <mj-section background-color="#f0f9ff" padding="30px" border-radius="16px" border-left="4px solid #1939B7">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          AI Insights
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Recommendations for Next Month
        </mj-text>
        <mj-text font-size="14px" color="#334155" line-height="2.2">
          <span style="background: #10B981; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">1</span> {{recommendation_1}}<br>
          <span style="background: #3B82F6; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">2</span> {{recommendation_2}}<br>
          <span style="background: #8B5CF6; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">3</span> {{recommendation_3}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Ready to Dive Deeper?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Access your full analytics dashboard for detailed insights and custom reports.
        </mj-text>
        <mj-button background-color="#2DD4BF" color="#0F172A" href="{{dashboard_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          View Full Dashboard &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "monthlyAnalytics",
  name: "Monthly Analytics - Premium",
  category: "pulse",
  purpose: "pulse" as const,
  description:
    "A world-class monthly analytics report with milestone achievements, colorful metric cards, channel/device breakdown, ranked query topics table, and AI-powered recommendations.",
  features: [
    "Monthly milestone celebration badge",
    "Colorful metric cards with trends",
    "Channel and device breakdown cards",
    "Ranked top queries with resolution rates",
    "AI-powered recommendations",
    "Premium CTA styling",
  ],
  subject: "&#128202; Monthly Analytics Report - {{month_name}} {{year}}",
  subTitle: "In-depth analysis of your chatbot performance",
  mjmlTemplate: monthlyAnalytics,
  tags: ["pulse", "monthly", "analytics", "report", "trends", "insights"],
};
